import { useId, useState } from "react";

import {
  AppDialog,
  AppDialogActions,
  DialogActionsSpacer,
  DialogPrimaryButton,
  DialogSecondaryButton,
  DialogTertiaryButton,
} from "@/components/ui/app-dialog";
import { focusPaperThenFirstInput } from "@/components/ui/dialog-focus";
import { RichTextField } from "@/components/ui/rich-text-field";
import { specNameError } from "@/components/ui/spec-name-rules";
import { TextField } from "@/components/ui/text-field";

// The donor's name-and-note form — `GenericNameNoteDialogForm`
// (`components/specSheets/GenericNameNoteDialogForm.tsx`), the dialog the
// Editor's Add group and Edit group / note open (`GroupDialogForm.tsx`, #805)
// and Edit Selection reuses (#807). Shared in the source, so shared here
// (`docs/agents/migration-guidelines.md`, "Shared donor components port as
// shared Atlas components").
//
// **The frame**: the donor's shared `Dialog`, `fullWidth` at MUI's default
// `sm` — 600px — with the `h2` title and the close X; the first input focused
// 50ms after the paper has entered (`onEntered: focusFirstInput`), and **no
// focus handed back on the close** (`disableRestoreFocus`). Escape, the
// backdrop and the X close it even mid-save, as the donor's do.
//
// **The fields** (`spacing={2}`, `py: 2`): `Name *` with react-hook-form's
// two rules — required, "This field is required"; at most 50, "Max length is
// 50 characters", a rule and not a `maxlength`, so a longer name can be typed —
// checked on submit and, once a submit has been tried, on every change
// (`mode: 'onSubmit'`, `reValidateMode: 'onChange'`); held while saving. Then
// the shared rich-text `Note`.
//
// **The actions**: `Clear Note` (tertiary, pushed left by a spacer, never
// held), `Cancel` (held while saving), then the submit, loading and held while
// saving — and held once the name has been emptied. That last flag is the
// donor's `isNameEmpty`, exactly: `false` to start, set on every Name change
// to `value.trim() === ''`, never reset by a re-seed or a close. So an empty
// Add starts with its submit live (submitting it shows the required message),
// and an Edit dialog that was emptied and cancelled opens held next time.
//
// **The values** belong to the form, as the donor's `useForm` does: seeded
// from `seed` whenever that object changes (the donor's Edit seeds with
// `useEffect([group])`), kept across a close otherwise, and submitted as typed
// — no trim. A caller that wants a fresh form per opening (Add, which the donor
// mounts only while open and resets on exit) remounts it from `onExited`.

export interface NameNoteValues {
  name: string;
  notes: string;
}

export interface NameNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  submitLabel: string;
  /** `Clear Note` at the footer's left; on, as the donor defaults it. */
  enableClearNote?: boolean;
  /** The write is in flight: the submit loads; Cancel and Name are held. */
  saving?: boolean;
  /** What the form shows — written in whenever this object changes. */
  seed: NameNoteValues;
  /**
   * Whether a new seed also forgets a tried submit — react-hook-form's
   * `reset`, which Edit Selection seeds with (`EditSelectionDetails.tsx:29-34`).
   * Off by default: Edit group seeds with `setValue` (`EditGroup.tsx:31-35`),
   * which keeps checking the name as it is typed once a submit was tried.
   */
  resetSubmitOnSeed?: boolean;
  onSubmit: (values: NameNoteValues) => void;
  /** The paper has gone — the donor's `onExited`. */
  onExited?: () => void;
}

export function NameNoteDialog({
  open,
  onOpenChange,
  title,
  submitLabel,
  enableClearNote = true,
  saving = false,
  seed,
  resetSubmitOnSeed = false,
  onSubmit,
  onExited,
}: NameNoteDialogProps) {
  const formId = useId();
  const [values, setValues] = useState(seed);
  const [isNameEmpty, setIsNameEmpty] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // The donor's seeding in `useEffect([group | selection])`: a new seed object
  // is written in, whatever was typed — and, for a caller that seeds with
  // `reset`, the tried submit goes with it (`resetSubmitOnSeed`, #807). Adjusted
  // during render rather than in an effect.
  const [seeded, setSeeded] = useState(seed);
  if (seeded !== seed) {
    setSeeded(seed);
    setValues(seed);
    if (resetSubmitOnSeed) setSubmitted(false);
  }

  const error = submitted ? specNameError(values.name) : undefined;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      onOpenAutoFocus={focusPaperThenFirstInput}
      restoreFocus={false}
      onCloseAutoFocus={() => onExited?.()}
      actions={
        <AppDialogActions>
          {enableClearNote ? (
            <>
              <DialogTertiaryButton
                onClick={() => setValues((current) => ({ ...current, notes: "" }))}
              >
                Clear Note
              </DialogTertiaryButton>
              <DialogActionsSpacer />
            </>
          ) : null}
          <DialogSecondaryButton disabled={saving} onClick={() => onOpenChange(false)}>
            Cancel
          </DialogSecondaryButton>
          <DialogPrimaryButton type="submit" form={formId} loading={saving} disabled={isNameEmpty}>
            {submitLabel}
          </DialogPrimaryButton>
        </AppDialogActions>
      }
    >
      <form
        id={formId}
        noValidate
        autoComplete="off"
        className="flex flex-col gap-4 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (saving || specNameError(values.name) !== undefined) return;
          onSubmit(values);
        }}
      >
        <TextField
          label="Name"
          required
          value={values.name}
          onChange={(name) => {
            setIsNameEmpty(name.trim() === "");
            setValues((current) => ({ ...current, name }));
          }}
          disabled={saving}
          error={error}
          // The donor's Name item: `mt: 1` above it, and `sm={12}` with nothing
          // below `sm` (`GenericNameNoteDialogForm.tsx:53`), so under 600px the
          // item shrinks to its field's own width — 209.5px, measured on the
          // running donor at a 480px viewport. Stated rather than left to the
          // input's intrinsic size, which this app's Inter Variable metrics put
          // at 215px. Never past the form, as the donor's item shrinks
          // (`flex: 0 1 auto`) on a phone too narrow for it.
          className="mt-2 w-full max-sm:w-[209.5px] max-sm:max-w-full"
        />
        <RichTextField
          label="Note"
          disabled={saving}
          value={values.notes}
          onChange={(notes) => setValues((current) => ({ ...current, notes }))}
        />
      </form>
    </AppDialog>
  );
}
